import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { getRendererByName } from '../chat/panel/renderers';

interface ToolCall {
  tool_name: string;
  arguments: string;
  result: string;
  duration_ms: number;
}

interface AdminContextPanelProps {
  toolCalls: ToolCall[];
}

const AdminContextPanel: React.FC<AdminContextPanelProps> = ({ toolCalls }) => {
  const { darkMode } = useTheme();
  const [selectedIdx, setSelectedIdx] = useState(toolCalls.length - 1);

  useEffect(() => {
    setSelectedIdx(toolCalls.length - 1);
  }, [toolCalls.length]);

  const selectedCall = toolCalls[selectedIdx] || null;
  const Renderer = selectedCall ? getRendererByName(selectedCall.tool_name) : null;

  return (
    <div
      className={`h-full flex flex-col ${
        darkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-50 text-gray-800'
      }`}
    >
      <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Context</h3>
      </div>

      {toolCalls.length > 1 && (
        <div
          className={`flex overflow-x-auto px-2 py-1.5 border-b gap-1 ${
            darkMode ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          {toolCalls.map((tc, i) => (
            <button
              key={i}
              onClick={() => setSelectedIdx(i)}
              className={`px-2 py-1 rounded text-[10px] font-mono whitespace-nowrap transition-colors ${
                i === selectedIdx
                  ? darkMode
                    ? 'bg-gray-700 text-white'
                    : 'bg-gray-200 text-gray-800'
                  : darkMode
                  ? 'text-gray-500 hover:text-gray-300'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {tc.tool_name.replace('admin_', '')}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {!selectedCall || !Renderer ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">
              Tool results will appear here as the Admin Assistant uses tools.
            </p>
            <div className="mt-6 space-y-1">
              <p className="text-xs text-gray-400 mb-2">Try asking:</p>
              <p className="text-xs text-gray-500 italic">"Show me a work dashboard"</p>
              <p className="text-xs text-gray-500 italic">"What needs attention?"</p>
              <p className="text-xs text-gray-500 italic">"Give me a weekly standup"</p>
            </div>
          </div>
        ) : (
          <div>
            <div
              className={`text-xs font-mono px-2 py-1 rounded mb-3 ${
                darkMode ? 'bg-gray-800' : 'bg-gray-200'
              }`}
            >
              {selectedCall.tool_name} ({selectedCall.duration_ms}ms)
            </div>
            <Renderer toolCall={selectedCall} darkMode={darkMode} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContextPanel;
